import React, { useMemo, useState } from 'react'
import { Modal } from '@mui/material'
import { Formik, Form } from 'formik'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Button } from 'src/components/shared/Button'
import { UpdateContractorFile } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { useDispatch } from 'react-redux'
import { EditContractorFileExpirationDateSchema } from './EditContractorFileExpirationDateForm.validator'
import { EditContractorFileExpirationDateForm } from './EditContractorFileExpirationDateForm.interface'
import { ContractorFile } from 'src/interfaces/file'
import { FormikDatePicker } from 'src/components/shared/form/formik/FormikDatePicker'
import dayjs from 'dayjs'

interface Props {
  isOpen: boolean
  contractorId: string
  contractorFile: ContractorFile
  onClose(): void
  onUpdate(): void | Promise<void>
}

export const EditContractorFileExpirationDateModal = ({
  isOpen,
  contractorId,
  contractorFile,
  onClose,
  onUpdate,
}: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)

  const initialValues: EditContractorFileExpirationDateForm = useMemo(
    () => ({
      expiresAt: contractorFile.expiresAt || '',
    }),
    [contractorFile.expiresAt],
  )

  const handleSubmit = async (values: EditContractorFileExpirationDateForm) => {
    try {
      setIsLoading(true)

      await createAsyncAction(
        dispatch,
        UpdateContractorFile.request({
          contractorId,
          fileId: contractorFile.id,
          expiresAt: values.expiresAt,
          name: contractorFile.name,
        }),
      )

      showToast('File expiration date has been set!')

      onClose()
      await onUpdate()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="file-modal-title"
      aria-describedby="file-modal-description"
      css={tw`fixed inset-0 flex items-center justify-center`}
    >
      <div css={tw`w-[336px] bg-white flex flex-col rounded-lg shadow-lg`}>
        <ModalHeader className={tw`h-11`} title="Set expiration date" onClose={onClose} />
        <Formik
          initialValues={initialValues}
          validationSchema={EditContractorFileExpirationDateSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form css={tw`flex flex-col flex-1`}>
              <div css={tw`p-4 flex-1 overflow-y-auto`}>
                <FormikDatePicker
                  name="expiresAt"
                  label="Select date of expiration"
                  minDate={dayjs().add(1, 'day')}
                  minDateError="Expiration date should be at least 1 day in the future"
                />
              </div>
              <div
                css={tw`px-4 py-3 flex justify-between border-0 border-t border-solid border-[#EDEDED]`}
              >
                <Button
                  variant="contained"
                  color="grey"
                  size="small"
                  onClick={onClose}
                  loading={isLoading}
                  className={tw`w-[100px]`}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  type="submit"
                  loading={isLoading}
                  className={tw`w-[120px]`}
                >
                  Save
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}
